import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    Output
} from "@angular/core";
import {FedeltaImageData} from "@fedelta-pos/data-client";
import {SessionModel} from "../../orchestration/session.model";
import {Subscription} from "rxjs";
import {TranslateService} from "ng2-translate";
import {AnalyticsService} from "../../orchestration/analytics.service";
import {ButtonProminence, ButtonTier} from "../../buttons/button/button";
import {PromoModel} from "../promo.model";
import {MenuEvents} from "../../menu/menu-events";
import {OrderEvents} from "../../order/order-events";
import {QrCodePageComponent} from "../../ui-elements/qr-code/qr-code-page/qr-code-page.component";
import {NavigationService} from "../../orchestration/navigation.service";
import {IconButtonType} from "../../buttons/icon-button/icon-button";
import {StringUtils} from "../../string-utils/string-utils";
import {MenuModel} from "../../menu/menu.model";
import {SessionEvents} from "../../orchestration/session-events";
import {AppPromoAction, AppPromoData, AppPromoPromotedSellableData} from "../../identity/member-summary-entity.model";
import {AppDealActionStyle, AppPromoTextPosition} from "../../orchestration/app-entity.model";
import {EntitlementData} from "../../deal/entitlement-entity.model";
import {MenuItemData} from "../../menu/menu-entity.model";

@Component({
    selector: "woa-promo-card",
    templateUrl: "./promo-card.component.html"
})
export class PromoCardComponent implements OnInit, OnDestroy {
    static readonly PROMOTED_PRODUCT_CYCLE_SECONDS: number = 10;

    @Input() title: string;
    @Input() message: string;
    @Input() image: string | FedeltaImageData;
    @Input() autoCropImage: boolean = false;
    @Input() actionLabel: string;
    @Input() actionStyle: AppDealActionStyle;
    @Input() empty: boolean = false;
    @Input() suppressProducts: boolean = true;
    @Input() showCode: boolean = false;
    @Input() showCodeLink: boolean = false;

    @Output() onActivate: EventEmitter<MenuItemData> = new EventEmitter<MenuItemData>();

    ButtonTier = ButtonTier;
    ButtonProminence = ButtonProminence;
    IconButtonType = IconButtonType;
    AppPromoTextPosition = AppPromoTextPosition;
    StringUtils = StringUtils;

    public descriptionPosition: AppPromoTextPosition = AppPromoTextPosition.BOTTOM;
    public currentProductName: string;
    public currentProductPrice: number;
    public currentTitle: string;
    public currentMessage: string;
    public currentImage: string | FedeltaImageData;
    public autoCropCurrentImage: boolean;
    public currentActionLabel: string;
    public currentActionStyle: AppDealActionStyle = AppDealActionStyle.INLINE;
    public fadeOutTitle: boolean;
    public fadeOutProductDetails: boolean;
    public fadeOutMessage: boolean;
    public fadeOutImage: boolean;
    public cardHover: boolean = false;
    public actionHover: boolean = false;
    public menuItem: MenuItemData;
    private _promo: AppPromoData;
    private _entitlement: EntitlementData;
    private promotedMenuItems: MenuItemData[] = [];
    private promotedMenuItemsCycleTimer: any;
    private subscriptions: Subscription = new Subscription();
    private appSettingsReceived: boolean = false;

    constructor(private navigationService: NavigationService,
                private analyticsService: AnalyticsService,
                private element: ElementRef,
                private translate: TranslateService,
                private sessionModel: SessionModel,
                private menuModel: MenuModel,
                private promoModel: PromoModel,
                private changeDetectorRef: ChangeDetectorRef) {
    }

    @HostBinding("class.inlineAction") get inlineAction(): boolean {
        return this.currentActionStyle === AppDealActionStyle.INLINE;
    }

    @HostBinding("class.cornerAction") get cornerAction(): boolean {
        return this.currentActionStyle === AppDealActionStyle.CORNER;
    }

    @HostBinding("class.peelAction") get peelAction(): boolean {
        return this.currentActionStyle === AppDealActionStyle.PEEL && !!this.currentActionLabel;
    }

    @HostBinding("class.bottomDescription") get bottomDescription(): boolean {
        return this.descriptionPosition === AppPromoTextPosition.BOTTOM;
    }

    @HostBinding("class.fadeOut") get fadeOut(): boolean {
        return this.fadeOutImage;
    }

    @HostBinding("class.withAction") get withAction(): boolean {
        return !!this.currentActionLabel;
    }

    @HostBinding("class.typeSpecific") currentRestrictedOrderTypes: string;

    get imageData(): FedeltaImageData {
        return <FedeltaImageData>this.currentImage;
    }

    get promo(): AppPromoData {
        return this._promo;
    }

    @Input()
    set promo(promo: AppPromoData) {
        this._promo = promo;
        this.refresh();
    }

    public get entitlement(): EntitlementData {
        return this._entitlement;
    }

    @Input()
    public set entitlement(entitlement: EntitlementData) {
        this._entitlement = entitlement;
        this.refresh();
    }

    public get qr_code(): string {
        return this._entitlement ? this._entitlement.externalId : undefined;
    }

    get hasImage(): boolean {
        return !!this.currentImage && (typeof this.currentImage === "string" || !!this.imageData.id || !!this.imageData.image);
    }

    get hasDescription(): boolean {
        return !!this.currentTitle || !!this.currentProductName || !!this.currentMessage;
    }

    get shadeImage(): boolean {
        return this.hasImage && this.autoCropCurrentImage && this.hasDescription;
    }

    ngOnInit(): void {
        if (this.empty) {
            this.refresh();
        }
        this.subscriptions.add(SessionEvents.currentApp$.subscribe(() => {
            this.appSettingsReceived = true;
            this.refresh();
        }));
        this.subscriptions.add(MenuEvents.currentMenu.subscribe(() => {
            this.refresh();
        }));
        this.subscriptions.add(OrderEvents.orderChanged$.subscribe(() => {
            this.refresh();
        }));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private refresh(): void {

        // Avoid double application on creation of component
        if (!this.appSettingsReceived) {
            return;
        }

        this.descriptionPosition = this.sessionModel.currentApp.promoTextPosition;
        this.applyCardDetails();

        // Find menu items for promoted products
        if (this._promo && this._promo.promotedSellables.length && MenuEvents.currentMenu.value) {
            this.promotedMenuItems = this._promo.promotedSellables
                .map((promoted: AppPromoPromotedSellableData) => this.promoModel.getPromotableMenuItem(promoted, this.suppressProducts && this._promo.suppressPromoCardProducts))
                .filter((menuItem: MenuItemData) => menuItem);
            if (this.promotedMenuItemsCycleTimer) {
                clearTimeout(this.promotedMenuItemsCycleTimer);
            }
            if (this.promotedMenuItems.length) {
                this.displayPromotedMenuItem(0);
            }
        }
        this.changeDetectorRef.detectChanges();
    }

    private displayPromotedMenuItem(idx: number) {
        if (idx >= this.promotedMenuItems.length) {
            idx = 0;
        }
        const menuItem: MenuItemData = this.promotedMenuItems[idx];
        this.applyCardDetails(menuItem);
        if (this.cyclePromotedMenuItems) {
            this.promotedMenuItemsCycleTimer = setTimeout(() => {
                this.displayPromotedMenuItem(this.cardHover || this.actionHover ? idx : idx + 1);
            }, PromoCardComponent.PROMOTED_PRODUCT_CYCLE_SECONDS * 1000);
        }
    }

    private get cyclePromotedMenuItems(): boolean {
        return this._promo && this.promotedMenuItems.length > 1 && (!this._promo.image.id || this._promo.showDetailsOnPromoCard);
    }

    private applyCardDetails(menuItem?: MenuItemData): void {
        let title: string;
        let productName: string;
        let productPrice: number;
        let message: string;
        let restrictedOrderTypes: string;
        let actionLabel: string;
        let actionStyle: AppDealActionStyle;

        if (this._promo) {
            title = !this._promo.showTitleOnPromoCard ? undefined
                : this._promo.appText1 ? this._promo.appText1
                    : this._promo.promotedSellables.length && MenuEvents.currentMenu.value ? this.translate.instant("woa.heading.promo.default")
                        : this._promo.name;
            message = this._promo.showMessageOnPromoCard ? this._promo.appText2 : undefined;
            productName = menuItem && this._promo.showDetailsOnPromoCard ? menuItem.name : undefined;
            productPrice = menuItem && this._promo.showDetailsOnPromoCard ? menuItem.sellable.defaultPrice : undefined;
            actionLabel = !this._promo.showActionButtonOnPromoCard ? undefined
                : menuItem && this._promo.promoCardAction ? this.translate.instant("woa.button.promo." + this._promo.promoCardAction)
                    : this.actionLabel;

            // noinspection JSIgnoredPromiseFromCall
            this.analyticsService.trackPromoShown(this._promo.name, "card",
                menuItem ? menuItem.name : undefined, menuItem ? menuItem.sellable.defaultPrice : undefined, menuItem ? menuItem.sellable.plu : undefined);
        } else if (this._entitlement) {
            title = this._entitlement.showTitleOnDealCard ? (this.entitlement.appText1 || this._entitlement.distributionChannel.distributionReference) : undefined;
            message = this._entitlement.appText2;
            actionLabel = this._entitlement.showActionButtonOnDealCard ? this.translate.instant("woa.button.deals.orderDeal") : undefined;
            restrictedOrderTypes = this._entitlement.orderAhead && this._entitlement.orderDelivery && this._entitlement.orderAtTable ? undefined
                : this._entitlement.orderAhead && this._entitlement.orderDelivery ? "ahead-delivery"
                    : this._entitlement.orderAhead && this._entitlement.orderAtTable ? "ahead-atTable"
                        : this._entitlement.orderDelivery && this._entitlement.orderAtTable ? "delivery-atTable"
                            : this._entitlement.orderAhead ? "ahead"
                                : this._entitlement.orderDelivery ? "delivery"
                                    : this._entitlement.orderAtTable ? "atTable"
                                        : undefined;
        } else {
            title = this.title;
            message = this.message;
            actionLabel = this.actionLabel;
            menuItem = undefined;
        }
        actionStyle = this.actionStyle || this.sessionModel.currentApp.dealActionStyle;

        const {image, autoCropImage}: {
            image: string | FedeltaImageData,
            autoCropImage: boolean
        } = this.getImage(menuItem);

        this.fadeOutTitle = this.currentTitle !== title;
        this.fadeOutProductDetails = this.currentProductName !== productName;
        this.fadeOutMessage = this.currentMessage !== message;
        this.fadeOutImage = this.currentImage !== image && this.cyclePromotedMenuItems;
        this.changeDetectorRef.markForCheck();

        setTimeout(() => {
            this.menuItem = menuItem;
            this.fadeOutTitle = false;
            this.fadeOutProductDetails = false;
            this.fadeOutMessage = false;
            this.fadeOutImage = false;
            this.currentTitle = title;
            this.currentProductName = productName;
            this.currentProductPrice = productPrice;
            this.currentMessage = message;
            this.currentRestrictedOrderTypes = restrictedOrderTypes;
            this.currentImage = image;
            this.autoCropCurrentImage = autoCropImage;
            this.currentActionLabel = actionLabel;
            this.currentActionStyle = actionStyle;
            this.changeDetectorRef.markForCheck();
        }, 1000);
    }

    private getImage(menuItem: MenuItemData): { image: string | FedeltaImageData, autoCropImage: boolean } {
        if (this._promo && this._promo.image.id) {
            return {image: this._promo.image, autoCropImage: this._promo.imageAutoCrop};
        }
        if (menuItem) {
            const image: FedeltaImageData = this.menuModel.getItemImage(undefined, menuItem, !!this._promo);
            if (image) {
                return {image: image, autoCropImage: this._promo && this._promo.imageAutoCrop};
            }
        }
        if (this._entitlement && this._entitlement.image.id) {
            return {image: this._entitlement.image, autoCropImage: this._entitlement.imageAutoCrop};
        }
        if (this._entitlement && this._entitlement.template.image.id) {
            return {image: this._entitlement.template.image, autoCropImage: this._entitlement.template.imageAutoCrop};
        }
        if (this.image) {
            return {image: this.image, autoCropImage: this.autoCropImage};
        }
        if (this.sessionModel.currentApp.defaultImage) {
            return {image: "data:image;base64," + this.sessionModel.currentApp.defaultImage, autoCropImage: true};
        }
        return {image: undefined, autoCropImage: false};
    }

    ignore(event: MouseEvent) {
        event.preventDefault();
    }

    cardMouseOver(): void {
        this.cardHover = true;
    }

    cardMouseOut(): void {
        this.cardHover = false;
    }

    actionMouseOver(): void {
        this.actionHover = true;
    }

    actionMouseOut(): void {
        this.actionHover = false;
    }

    async showEntitlementQRCode(): Promise<void> {
        if (this._entitlement) {
            return QrCodePageComponent.showQrCode(this.navigationService, this._entitlement.externalId, this.translate.instant("woa.label.deals.qrCode"));
        }
    }

    performAction(onlyWhenNoButton: boolean = false): void {
        if (this._promo && !this._promo.promoCardAction) {
            return;
        }
        if (onlyWhenNoButton && this.currentActionLabel) {
            return;
        }
        if (this._promo) {
            switch (this._promo.promoCardAction) {
                case AppPromoAction.ORDER_PRODUCT:
                    if (!this.menuItem) {
                        return;
                    }

                    // noinspection JSIgnoredPromiseFromCall
                    this.analyticsService.trackPromoItemOrdered(this._promo.name, "card",
                        this.menuItem ? this.menuItem.name : undefined, this.menuItem ? this.menuItem.sellable.defaultPrice : undefined, this.menuItem ? this.menuItem.sellable.plu : undefined);
                    break;
                case AppPromoAction.SHOW_PRODUCTS:
                    // noinspection JSIgnoredPromiseFromCall
                    this.analyticsService.trackPromoItemsShown(this._promo.name, "card");
                    break;
            }
        }
        this.onActivate.emit(this.menuItem);
    }
}



// WEBPACK FOOTER //
// ./src/app/promo/promo-card/promo-card.component.ts